import { useCallback, useMemo, useRef, useState } from 'react';

import { captureError, captureErrorMessage } from '../../../utils/capture';

const POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export type PolledEntryIntent = {
  confirmedAt?: string | null;
  discardedAt?: string | null;
  errorCode?: string | null;
};

export type EntryIntentRead<TIntent extends PolledEntryIntent> = {
  error?: unknown;
  intent?: TIntent | null;
};

export type EntryIntentPollingArgs<TIntent extends PolledEntryIntent> = {
  label: string;
  onConfirmed: (intent: TIntent) => Promise<void>;
  read: (intentId: string) => Promise<EntryIntentRead<TIntent>>;
};

export type EntryIntentPollingController = {
  confirmEntry: (intentId: string) => Promise<boolean>;
  confirming: boolean;
};

const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const useEntryIntentPolling = <TIntent extends PolledEntryIntent>({
  label,
  onConfirmed,
  read,
}: EntryIntentPollingArgs<TIntent>): EntryIntentPollingController => {
  const [confirming, setConfirming] = useState(false);
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  const confirmEntry = useCallback(
    async (intentId: string): Promise<boolean> => {
      setConfirming(true);

      try {
        for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
          if (attempt > 0) {
            await wait(POLL_INTERVAL_MS);
          }

          const { error, intent } = await read(intentId);

          if (error) {
            captureError(error);
            return false;
          }

          if (!intent) {
            captureErrorMessage(`${label} ${intentId} could not be read`);
            return false;
          }

          if (intent.errorCode) {
            captureErrorMessage(
              `${label} ${intentId} failed with ${intent.errorCode}`,
            );
            return false;
          }

          if (intent.discardedAt) {
            captureErrorMessage(`${label} ${intentId} was discarded`);
            return false;
          }

          if (intent.confirmedAt) {
            try {
              await onConfirmedRef.current(intent);
            } catch (confirmedError) {
              captureError(confirmedError);
            }

            return true;
          }
        }

        captureErrorMessage(
          `${label} ${intentId} was not confirmed within ${POLL_ATTEMPTS} attempts`,
        );

        return false;
      } catch (pollError) {
        captureError(pollError);
        return false;
      } finally {
        setConfirming(false);
      }
    },
    [label, read],
  );

  return useMemo(
    () => ({ confirmEntry, confirming }),
    [confirmEntry, confirming],
  );
};
